/**
 * Admin → Settings → Jev → Question sets — E07-F12-T02 (real adapter: GET/PUT
 * /admin/jev/question-sets, admin-only).
 *
 * Screens:
 *   jev-question-sets — the three sets (research_triage / research_verdict /
 *                       research_same_company) with calibration status;
 *   jev-set-editor    — one set: questions (answer kind, instruction, criteria:
 *                       options for choice, levels with anchors for score), band
 *                       rules, state limits; add / edit / remove a question; the
 *                       form check names each error (same rules as
 *                       `jev_decide.py lint`); before saving, a warning that the
 *                       set's calibration resets.
 *
 * The questions are English by design (§5.2): request data travels in the state,
 * so the questions stay constant and the calibration survives from request to
 * request.
 */
import { useMemo, useState } from 'react';
import { Dialog, DialogFooter, useToast } from '../ui/index.js';
import { errorMessage } from '../api/client.js';
import { useJevQuestionSets, useSaveJevQuestionSet, type JevQuestionSet } from './jevApi.js';
import {
  ANSWER_KIND_LABEL,
  defaultCriteria,
  formatDate,
  lintQuestionSet,
  type JevAnswerKind,
  type JevBandRule,
  type JevCriterion,
  type JevLintError,
  type JevQuestion,
} from './jevModel.js';
import {
  BTN_OUTLINE,
  BTN_OUTLINE_PRIMARY,
  BTN_PRIMARY,
  Card,
  FIELD_CLASS,
  Loading,
  Notice,
  Pill,
  SectionHeading,
} from './jevUi.js';

// ---------------------------------------------------------------------------
// List
// ---------------------------------------------------------------------------

function CalibrationPill({ set }: { readonly set: JevQuestionSet }): React.JSX.Element {
  return set.calibrated ? (
    <Pill tone="success" testId={`jev-set-${set.key}-calibrated`}>
      <i className="fa-solid fa-bullseye" aria-hidden /> Calibrated {formatDate(set.calibratedAt)}
    </Pill>
  ) : (
    <Pill tone="warning" testId={`jev-set-${set.key}-uncalibrated`}>
      <i className="fa-solid fa-shield-halved" aria-hidden /> Not calibrated — safe defaults
    </Pill>
  );
}

function SetList({
  sets,
  onEdit,
}: {
  readonly sets: readonly JevQuestionSet[];
  readonly onEdit: (key: JevQuestionSet['key']) => void;
}): React.JSX.Element {
  return (
    <div data-testid="jev-question-sets" className="grid gap-4">
      <SectionHeading
        title="Question sets"
        hint="Each set is what Jev is asked in one call. Editing a set resets its calibration: the old probabilities belonged to the old questions."
      />
      {sets.map((s) => (
        <Card key={s.key} testId={`jev-set-${s.key}`}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h4 className="text-sm font-semibold text-text-strong">{s.title}</h4>
                <code className="rounded bg-gray-100 px-1.5 py-0.5 text-[11px] text-gray-700">
                  {s.key}
                </code>
                <CalibrationPill set={s} />
              </div>
              <p className="mt-1 text-xs text-text-muted">{s.purpose}</p>
            </div>
            <button
              type="button"
              data-testid={`jev-set-${s.key}-edit`}
              onClick={() => onEdit(s.key)}
              className={BTN_OUTLINE_PRIMARY}
            >
              <i className="fa-solid fa-pen mr-1.5" aria-hidden />
              Edit questions
            </button>
          </div>
          <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 border-t border-border pt-3 text-[11px] text-text-muted">
            <span>
              <strong className="text-text-strong">{s.questions.length}</strong> question
              {s.questions.length === 1 ? '' : 's'}:{' '}
              <span className="font-mono">{s.questions.map((q) => q.name).join(', ')}</span>
            </span>
            <span>
              <strong className="text-text-strong">{s.bandRules.length}</strong> band rule
              {s.bandRules.length === 1 ? '' : 's'}
            </span>
            {s.limits.length > 0 ? (
              <span className="font-mono">
                {s.limits.map((l) => `${l.name} ${l.value.toLocaleString('en-US')}`).join(' · ')}
              </span>
            ) : null}
            <span>
              Edited {formatDate(s.updatedAt)} by {s.updatedBy}
            </span>
          </div>
        </Card>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Editor
// ---------------------------------------------------------------------------

function errorsFor(errors: readonly JevLintError[], where: string): JevLintError[] {
  return errors.filter((e) => e.where === where);
}

function CriteriaEditor({
  q,
  index,
  onChange,
}: {
  readonly q: JevQuestion;
  readonly index: number;
  readonly onChange: (criteria: JevCriterion[]) => void;
}): React.JSX.Element {
  const keyLabel = q.kind === 'score' ? 'Level' : q.kind === 'noul' ? 'Answer' : 'Option';
  const anchorLabel =
    q.kind === 'score' ? 'Anchor (what this level means)' : 'Criterion (when to answer this)';
  return (
    <div className="grid gap-2">
      <div className="grid grid-cols-[6.5rem_minmax(0,1fr)_2rem] gap-2 text-[11px] font-medium text-text-muted">
        <span>{keyLabel}</span>
        <span>{anchorLabel}</span>
        <span />
      </div>
      {q.criteria.map((c, ci) => (
        <div key={ci} className="grid grid-cols-[6.5rem_minmax(0,1fr)_2rem] items-start gap-2">
          <input
            aria-label={`${keyLabel} ${ci + 1}`}
            data-testid={`jev-q${index}-crit${ci}-key`}
            className={`${FIELD_CLASS} font-mono`}
            value={c.key}
            onChange={(e) =>
              onChange(q.criteria.map((x, i) => (i === ci ? { ...x, key: e.target.value } : x)))
            }
          />
          <input
            aria-label={`${anchorLabel} ${ci + 1}`}
            data-testid={`jev-q${index}-crit${ci}-anchor`}
            className={FIELD_CLASS}
            value={c.anchor}
            onChange={(e) =>
              onChange(q.criteria.map((x, i) => (i === ci ? { ...x, anchor: e.target.value } : x)))
            }
          />
          <button
            type="button"
            aria-label={`Remove ${keyLabel.toLowerCase()} ${ci + 1}`}
            data-testid={`jev-q${index}-crit${ci}-remove`}
            onClick={() => onChange(q.criteria.filter((_, i) => i !== ci))}
            className="mt-1.5 rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600"
          >
            <i className="fa-solid fa-xmark" aria-hidden />
          </button>
        </div>
      ))}
      <button
        type="button"
        data-testid={`jev-q${index}-crit-add`}
        onClick={() =>
          onChange([
            ...q.criteria,
            { key: q.kind === 'score' ? String(q.criteria.length) : '', anchor: '' },
          ])
        }
        className="justify-self-start text-xs font-medium text-link hover:underline"
      >
        + Add {keyLabel.toLowerCase()}
      </button>
    </div>
  );
}

function QuestionCard({
  q,
  index,
  open,
  errors,
  onToggle,
  onChange,
  onRemove,
}: {
  readonly q: JevQuestion;
  readonly index: number;
  readonly open: boolean;
  readonly errors: readonly JevLintError[];
  readonly onToggle: () => void;
  readonly onChange: (q: JevQuestion) => void;
  readonly onRemove: () => void;
}): React.JSX.Element {
  const hasErrors = errors.length > 0;
  return (
    <div
      data-testid={`jev-question-${index}`}
      className={`rounded-lg border bg-white ${hasErrors ? 'border-red-300 ring-1 ring-red-200' : 'border-border'}`}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        data-testid={`jev-question-${index}-toggle`}
        className="flex w-full items-center gap-3 px-4 py-3 text-left"
      >
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[var(--color-surface-muted)] text-[11px] font-semibold text-text-strong">
          {index + 1}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate font-mono text-sm text-text-strong">
            {q.name || 'unnamed_question'}
          </span>
          <span className="block truncate text-[11px] text-text-muted">
            {q.instructions || 'No instruction yet'}
          </span>
        </span>
        <Pill tone="info">{ANSWER_KIND_LABEL[q.kind]}</Pill>
        <span className="hidden text-[11px] text-text-muted sm:inline">
          {q.criteria.length} {q.kind === 'score' ? 'levels' : 'criteria'}
        </span>
        {hasErrors ? (
          <Pill tone="danger">
            {errors.length} error{errors.length === 1 ? '' : 's'}
          </Pill>
        ) : null}
        <i
          className={`fa-solid fa-chevron-${open ? 'up' : 'down'} text-xs text-gray-400`}
          aria-hidden
        />
      </button>

      {open ? (
        <div className="grid gap-4 border-t border-border px-4 py-4">
          <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_12rem]">
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-text-strong">
                Name (sent to Jev)
              </span>
              <input
                data-testid={`jev-q${index}-name`}
                className={`${FIELD_CLASS} font-mono`}
                value={q.name}
                placeholder="e.g. company_type"
                onChange={(e) => onChange({ ...q, name: e.target.value })}
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-text-strong">Answer kind</span>
              <select
                data-testid={`jev-q${index}-kind`}
                className={FIELD_CLASS}
                value={q.kind}
                onChange={(e) => {
                  const kind = e.target.value as JevAnswerKind;
                  onChange({ ...q, kind, criteria: defaultCriteria(kind) });
                }}
              >
                {(Object.keys(ANSWER_KIND_LABEL) as JevAnswerKind[]).map((k) => (
                  <option key={k} value={k}>
                    {ANSWER_KIND_LABEL[k]}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-text-strong">Instruction</span>
            <textarea
              data-testid={`jev-q${index}-instructions`}
              rows={2}
              className={FIELD_CLASS}
              value={q.instructions}
              onChange={(e) => onChange({ ...q, instructions: e.target.value })}
            />
          </label>
          <div>
            <span className="mb-2 block text-xs font-medium text-text-strong">
              {q.kind === 'score'
                ? 'Score levels'
                : q.kind === 'noul'
                  ? 'Criteria (exactly two: yes and no)'
                  : 'Options'}
            </span>
            <CriteriaEditor
              q={q}
              index={index}
              onChange={(criteria) => onChange({ ...q, criteria })}
            />
          </div>
          {hasErrors ? (
            <ul className="grid gap-1" data-testid={`jev-q${index}-errors`}>
              {errors.map((e, i) => (
                <li key={i} className="text-xs text-red-600">
                  <i className="fa-solid fa-circle-exclamation mr-1" aria-hidden />
                  {e.message}
                </li>
              ))}
            </ul>
          ) : null}
          <div className="flex justify-end">
            <button
              type="button"
              data-testid={`jev-q${index}-remove`}
              onClick={onRemove}
              className="rounded border border-red-200 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50"
            >
              <i className="fa-solid fa-trash-can mr-1.5" aria-hidden />
              Remove question
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function newQuestion(): JevQuestion {
  return { name: '', kind: 'noul', instructions: '', criteria: defaultCriteria('noul') };
}

function SetEditor({
  initial,
  onClose,
}: {
  readonly initial: JevQuestionSet;
  readonly onClose: () => void;
}): React.JSX.Element {
  const save = useSaveJevQuestionSet();
  const toast = useToast();
  const [draft, setDraft] = useState<JevQuestionSet>(initial);
  const [openIdx, setOpenIdx] = useState<Set<number>>(() => new Set([0]));
  const [showErrors, setShowErrors] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const errors = useMemo(() => lintQuestionSet(draft), [draft]);
  const visibleErrors = showErrors ? errors : [];
  const dirty = JSON.stringify(draft) !== JSON.stringify(initial);

  function patch(change: Partial<JevQuestionSet>): void {
    setServerError(null);
    setDraft((d) => ({ ...d, ...change }));
  }

  function setQuestion(i: number, q: JevQuestion): void {
    patch({ questions: draft.questions.map((x, idx) => (idx === i ? q : x)) });
  }

  function removeQuestion(i: number): void {
    patch({ questions: draft.questions.filter((_, idx) => idx !== i) });
    setOpenIdx(new Set());
  }

  function addQuestion(): void {
    patch({ questions: [...draft.questions, newQuestion()] });
    setOpenIdx(new Set([draft.questions.length]));
  }

  function setRule(i: number, r: JevBandRule): void {
    patch({ bandRules: draft.bandRules.map((x, idx) => (idx === i ? r : x)) });
  }

  function trySave(): void {
    setShowErrors(true);
    if (errors.length > 0) {
      const firstQ = errors.find((e) => e.where.startsWith('question:'));
      if (firstQ) setOpenIdx((s) => new Set([...s, Number(firstQ.where.split(':')[1])]));
      return;
    }
    setConfirmOpen(true);
  }

  function doSave(): void {
    setConfirmOpen(false);
    save.mutate(draft, {
      onSuccess: () => {
        toast.success(`${draft.key} saved — not calibrated until recalibration`);
        onClose();
      },
      onError: (err) => setServerError(errorMessage(err, 'Could not save the question set')),
    });
  }

  const generalErrors = visibleErrors.filter(
    (e) => e.where === 'set' || e.where === 'limits' || e.where.startsWith('rules:'),
  );

  return (
    <div data-testid="jev-set-editor" className="grid gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button
          type="button"
          data-testid="jev-set-editor-back"
          onClick={onClose}
          className="text-xs font-medium text-link hover:underline"
        >
          <i className="fa-solid fa-arrow-left mr-1" aria-hidden />
          All question sets
        </button>
        <CalibrationPill set={initial} />
      </div>

      <Card>
        <SectionHeading
          title={draft.title}
          hint={
            <>
              <code className="rounded bg-gray-100 px-1 text-[11px]">{draft.key}</code> ·{' '}
              {draft.purpose} Questions are written in English; request data (grades, industries,
              states) goes into the state, not into a question.
            </>
          }
        />

        <div className="grid gap-3">
          {draft.questions.map((q, i) => (
            <QuestionCard
              key={i}
              q={q}
              index={i}
              open={openIdx.has(i)}
              errors={errorsFor(visibleErrors, `question:${i}`)}
              onToggle={() =>
                setOpenIdx((s) => {
                  const n = new Set(s);
                  if (n.has(i)) n.delete(i);
                  else n.add(i);
                  return n;
                })
              }
              onChange={(next) => setQuestion(i, next)}
              onRemove={() => removeQuestion(i)}
            />
          ))}
          <button
            type="button"
            data-testid="jev-question-add"
            onClick={addQuestion}
            className={`${BTN_OUTLINE} justify-self-start`}
          >
            <i className="fa-solid fa-plus mr-1.5" aria-hidden />
            Add question
          </button>
        </div>
      </Card>

      <Card testId="jev-band-rules">
        <SectionHeading
          title="Band rules"
          hint='Turn the answers into a band. Rules are checked top to bottom; the condition is JSON — question → {"lte" | "gte" | "winner_in" | "top_gte" | "confidence_gte"}, plus "agree" checks of a choice against a yes/no. Exactly one fallback band, the last one, with no condition.'
        />
        {draft.bandRules.length === 0 ? (
          <p className="text-xs text-text-muted">
            No bands — this set only shows a hint next to the row.
          </p>
        ) : null}
        <div className="grid gap-2">
          {draft.bandRules.map((r, i) => (
            <div
              key={i}
              data-testid={`jev-rule${i}`}
              className="grid items-start gap-2 md:grid-cols-[11rem_minmax(0,1fr)_7rem_2rem]"
            >
              <input
                aria-label={`Band of rule ${i + 1}`}
                data-testid={`jev-rule${i}-band`}
                className={`${FIELD_CLASS} font-mono`}
                value={r.name}
                placeholder="band_name"
                onChange={(e) => setRule(i, { ...r, name: e.target.value })}
              />
              <input
                aria-label={`Condition of rule ${i + 1}`}
                data-testid={`jev-rule${i}-when`}
                className={`${FIELD_CLASS} font-mono text-xs`}
                value={r.when}
                placeholder={r.isDefault ? '(fallback — no condition)' : '{"question":{"gte":0.7}}'}
                onChange={(e) => setRule(i, { ...r, when: e.target.value })}
              />
              <label className="mt-1.5 flex items-center gap-1.5 text-xs text-text-strong">
                <input
                  type="checkbox"
                  data-testid={`jev-rule${i}-default`}
                  checked={r.isDefault}
                  onChange={(e) => setRule(i, { ...r, isDefault: e.target.checked })}
                />
                Fallback
              </label>
              <button
                type="button"
                aria-label={`Remove rule ${i + 1}`}
                onClick={() => patch({ bandRules: draft.bandRules.filter((_, idx) => idx !== i) })}
                className="mt-1.5 rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600"
              >
                <i className="fa-solid fa-xmark" aria-hidden />
              </button>
            </div>
          ))}
        </div>
        <button
          type="button"
          data-testid="jev-rule-add"
          onClick={() =>
            patch({
              bandRules: [
                ...draft.bandRules.filter((r) => !r.isDefault),
                { name: '', isDefault: false, when: '' },
                ...draft.bandRules.filter((r) => r.isDefault),
              ],
            })
          }
          className="mt-3 text-xs font-medium text-link hover:underline"
        >
          + Add rule
        </button>
      </Card>

      <Card testId="jev-limits">
        <SectionHeading
          title="State limits"
          hint="How much goes into one call. Text is cut only by these limits (characters or item counts; the names are fixed by the code that builds the state)."
        />
        {draft.limits.length === 0 ? (
          <p className="text-xs text-text-muted">This set has no state limits.</p>
        ) : null}
        <div className="grid gap-4 sm:grid-cols-3">
          {draft.limits.map((l, li) => (
            <label key={l.name} className="block">
              <span className="mb-1 block font-mono text-xs font-medium text-text-strong">
                {l.name}
              </span>
              <input
                type="number"
                min={0}
                data-testid={`jev-limit-${l.name}`}
                className={FIELD_CLASS}
                value={Number.isFinite(l.value) ? l.value : ''}
                onChange={(e) =>
                  patch({
                    limits: draft.limits.map((x, i) =>
                      i === li
                        ? { ...x, value: e.target.value === '' ? NaN : Number(e.target.value) }
                        : x,
                    ),
                  })
                }
              />
            </label>
          ))}
        </div>
      </Card>

      {generalErrors.length > 0 || (showErrors && errors.length > 0) || serverError ? (
        <div className="grid gap-2" data-testid="jev-set-errors">
          {showErrors && errors.length > 0 ? (
            <Notice tone="danger" testId="jev-set-lint">
              <strong>
                The set was not saved — {errors.length} problem{errors.length === 1 ? '' : 's'} to
                fix:
              </strong>
              <ul className="mt-1 list-disc pl-4">
                {errors.map((e, i) => (
                  <li key={i}>{e.message}</li>
                ))}
              </ul>
            </Notice>
          ) : null}
          {serverError ? (
            <Notice tone="danger" testId="jev-set-server-error">
              {serverError}
            </Notice>
          ) : null}
        </div>
      ) : null}

      <div className="sticky bottom-0 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-white px-4 py-3 shadow-sm">
        <span className="text-[11px] text-text-muted">
          {dirty ? 'Unsaved changes. Saving resets this set’s calibration.' : 'No changes yet.'}
        </span>
        <div className="flex gap-2">
          <button
            type="button"
            data-testid="jev-set-discard"
            onClick={() => {
              setDraft(initial);
              setShowErrors(false);
              setServerError(null);
            }}
            disabled={!dirty}
            className={BTN_OUTLINE}
          >
            Discard changes
          </button>
          <button
            type="button"
            data-testid="jev-set-save"
            onClick={trySave}
            disabled={!dirty || save.isPending}
            className={BTN_PRIMARY}
          >
            {save.isPending ? 'Saving…' : 'Save set'}
          </button>
        </div>
      </div>

      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Calibration of this set will reset"
        testId="jev-calibration-reset"
        maxWidth="md"
        footer={
          <DialogFooter
            onCancel={() => setConfirmOpen(false)}
            onConfirm={doSave}
            confirmLabel="Save and reset calibration"
            variant="danger"
            testId="jev-calibration-reset"
          />
        }
      >
        <div className="grid gap-3 text-sm text-text">
          <p>
            Saving new questions for{' '}
            <code className="rounded bg-gray-100 px-1 text-xs">{draft.key}</code> makes its stored
            probabilities useless — they answered the old questions.
          </p>
          <ul className="list-disc pl-5 text-xs text-text-muted">
            {draft.key === 'research_triage' ? (
              <li>The Probable junk bin switches off — the worst a row gets is Doubtful.</li>
            ) : null}
            {draft.key === 'research_verdict' ? (
              <li>The needs-human threshold for A/B goes back to its safe default.</li>
            ) : null}
            <li>
              Thresholds get a new version “reset to safe defaults”; earlier versions stay in the
              history.
            </li>
            <li>
              To calibrate again, run a recalibration from Thresholds — it makes paid Jev calls on
              the labelled rows and shows the count and price before it starts.
            </li>
          </ul>
          {!initial.calibrated ? (
            <p className="text-xs text-text-muted">
              This set is not calibrated yet, so nothing is lost now.
            </p>
          ) : null}
        </div>
      </Dialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Root
// ---------------------------------------------------------------------------

export function JevQuestionSets(): React.JSX.Element {
  const setsQuery = useJevQuestionSets();
  const [editingKey, setEditingKey] = useState<JevQuestionSet['key'] | null>(null);

  if (setsQuery.isLoading) return <Loading testId="jev-sets-loading" />;
  if (setsQuery.isError || !setsQuery.data) {
    return (
      <Notice tone="danger" testId="jev-sets-error">
        {errorMessage(setsQuery.error, 'Failed to load question sets')}
      </Notice>
    );
  }
  const editing = editingKey ? setsQuery.data.find((s) => s.key === editingKey) : undefined;
  if (editing)
    return <SetEditor key={editing.key} initial={editing} onClose={() => setEditingKey(null)} />;
  return <SetList sets={setsQuery.data} onEdit={setEditingKey} />;
}
